/** @file Mirrors bone, slot, IK, draw-order and event timelines onto -L/-R counterparts (keys verbatim). */
import type { AnimationDoc, Id, SlotTimelines } from '@pose2d/format/types';
import { copyJson } from './copyJson';
import type { SideCounterparts } from './sideCounterparts';

/** The mirrored timelines of an animation (deform is handled by mirrorDeform). */
export type MirroredTimelines = Pick<AnimationDoc, 'bones' | 'slots' | 'ik' | 'drawOrder' | 'events'>;

/** Copies every value of `table` under the counterpart of its key. */
function remap<T>(table: Record<Id, T>, to: (id: Id) => Id): Record<Id, T> {
  const out: Record<Id, T> = {};
  for (const [id, value] of Object.entries(table)) out[to(id)] = copyJson(value);
  return out;
}

/** Slot timelines moved to the counterpart slots, attachment-key placeholders swapped per source slot. */
function mirrorSlots(slots: AnimationDoc['slots'], sides: SideCounterparts): AnimationDoc['slots'] {
  const out: AnimationDoc['slots'] = {};
  for (const [slotId, timelines] of Object.entries(slots)) {
    const copy: SlotTimelines = copyJson(timelines);
    if (copy.attachment) copy.attachment = copy.attachment.map((k) => ({ f: k.f, v: k.v === null ? null : sides.placeholder(slotId, k.v) }));
    out[sides.slot(slotId)] = copy;
  }
  return out;
}

/** Bones, slots, IK (bendPositive flipped), draw order (slots swapped) and events (verbatim) of `src`, mirrored. */
export function mirrorTimelines(src: AnimationDoc, sides: SideCounterparts): MirroredTimelines {
  const ik = remap(src.ik, sides.ik);
  for (const timelines of Object.values(ik)) {
    if (timelines.bendPositive) timelines.bendPositive = timelines.bendPositive.map((k) => ({ f: k.f, v: !k.v }));
  }
  return {
    bones: remap(src.bones, sides.bone),
    slots: mirrorSlots(src.slots, sides),
    ik,
    drawOrder: src.drawOrder.map((k) => ({ f: k.f, order: k.order === null ? null : k.order.map((slotId) => sides.slot(slotId)) })),
    events: copyJson(src.events),
  };
}
